import React, { ChangeEvent, Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useStore } from 'react-redux'
import { alertError } from 'api'
import { ServiceUserType } from 'models/ServiceUsersTypes'
import Table from 'uiKit/table/Table'
import TableRow from 'uiKit/table/TableRow'
import TableCell from 'uiKit/table/TableCell'
import Checkbox from '../Checkbox/Checkbox'
import MultipleEditBar from '../MultipleEditBar/MultipleEditBar'
import CarersSelect from '../CarersSelect/CarersSelect'
import CarePlans from '../CarePlans/CarePlans'
import { setAllServiceUsersId, setSelectedServiceUsersId } from '../../actions/serviceUsersActions'
import { ExportConversations } from '../ExportConversations/ExportConversations'
import { InputEditor } from '../InputEditor/InputEditor'
import { DuplicateWarningMessage, TableScroll } from './ServiceUsersTable.styles'
import { deletePatientCarePlan, getServiceUsers, uploadPatientKnowledge } from '../../api/serviceUsersApi'
import {
  downloadCarePlanAudit,
  generateCarePlanAudit,
  getCarePlanAudit,
  getCarePlanAuditStatus,
  CarePlanAuditPayload,
} from '../../api/carePlanAuditApi'
import CarePlanAudit, { CarePlanAuditState } from '../CarePlanAudit/CarePlanAudit'
import AuditResult from '../AuditResult/AuditResult'
import { useBotUsageContext } from 'contexts/BotUsageContext'
import { USAGE_METRIC } from 'constants/usageLimits'
import { isQcsBot } from 'constants/qcs'
import {
  auditBlockedReasonForUser,
  CARE_PLAN_PACKAGE_DISABLED_REASON,
  formatCarePlanAuditQuotaReason,
  hasPendingCarePlan,
} from '../../helpers/carePlanAuditGate'
import { isBotPackageCarePlanEnabled } from 'tabs/home/api/usageLimits'
import { getCachedBotPackageAssignment } from 'tabs/home/helpers/orgPackages'

/**
 * QCS organisations drop two columns the page has no use for — conversation
 * logs (no chatbot) and care professionals (the audit is on the care plan, not
 * the carer) — so that width goes to the columns QCS is actually here for: the
 * care-plan filenames and the audit control.
 */
const CARE_PLANS_WIDTH = 268
const CARE_PLANS_WIDTH_QCS = 460
const AUDIT_COLUMN_WIDTH = 240

interface Props {
  botId: string,
  setDeleteUserModal: Dispatch<SetStateAction<{ isOpen: boolean, selectedUser: ServiceUserType | null }>>,
  searchValue?: string
  page?: number
  rowsPerPage?: number | string
  onUploadingChange?: (isUploading: boolean) => void
}

export const ServiceUsersTable: FC<Props> = ({
  botId,
  setDeleteUserModal,
  searchValue,
  rowsPerPage,
  onUploadingChange,
}) => {
  const store = useStore()
  // The audit result opens as a full-screen overlay (processing → report). It
  // holds the patient id so the overlay can follow that service user's real
  // audit status rather than a timer of its own.
  const [auditFor, setAuditFor] = useState<{
    id: number
    name: string
    filename: string
    carePlans: string[]
  } | null>(null)
  const auditSubject = (user: ServiceUserType) => ({
    id: user.id,
    name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
    filename: user.carePlans?.[0]?.originalFilename || 'care-plan.pdf',
    carePlans: (user.carePlans || []).map(plan => plan.originalFilename).filter(Boolean),
  })
  const serviceUsers = useSelector((state: any) => state.serviceUsersTab.serviceUsers) as ServiceUserType[]
  const { serviceUsersTab: { selectedServiceUsersId, currentPage } } = store.getState()
  const isChecked = useCallback((userId) => selectedServiceUsersId.includes(userId), [selectedServiceUsersId])
  const [uploadingUserId, setUploadingUserId] = useState<number | null>(null)

  const activeBot = useSelector((state: any) => state.activeBot)

  // Conversation logs are chat history. A QCS organisation has no chatbot, so
  // the column would only ever export an empty file.
  const isQcs = isQcsBot(activeBot || botId)
  const showConversationLogs = !isQcs

  // Package care-plan audit off → view/download only (grandfathered Service Users).
  // No assignment (CareBrain) → helper returns true → mutations stay enabled.
  const carePlanMutationsAllowed = isBotPackageCarePlanEnabled(
    activeBot?.packageDetails || getCachedBotPackageAssignment(activeBot?.id || botId),
  )

  // Care plan audit is what a QCS organisation buys the Service Users tab for.
  // It is not offered to CareBrain organisations, who have no allowance behind
  // it and reach the same capability through the chatbot.
  const canAudit = isQcs

  // CareBrain assigns carers to service users from this table; QCS does not —
  // its unit of work is the care plan document, not the carer.
  const showCareProfessionals = !isQcs

  // With Care Professionals gone, the QCS row has room to give the name and
  // care-plan columns a wider, even share.
  const nameColumnWidth = isQcs ? 200 : 160
  // Match CheckboxContainer (24×24) so First/Last name center with the checkbox
  // while the row stays flex-start for tall Care Plans.
  const NAME_CONTROL_HEIGHT = 24
  // Push checkbox + names below the care-plans card's top padding (1px border + 9px).
  const NAME_CONTROL_TOP_OFFSET = 10
  const carePlansColumnWidth = showConversationLogs ? CARE_PLANS_WIDTH : CARE_PLANS_WIDTH_QCS

  // The page's shared allowance read, so starting an audit updates the meter
  // above the table rather than leaving it claiming the old number.
  const usage = useBotUsageContext()
  const auditAllowance = usage?.metric(USAGE_METRIC.CARE_PLAN_AUDITS)

  // Session progress for audits started on this page (POST in flight / just
  // finished). Existing reports are hydrated from GET for the current page so
  // the ready "Care plan audit" control survives reload. History for the
  // overlay still comes from GET inside AuditResult.
  const [audits, setAudits] = useState<Record<number, CarePlanAuditState>>({})
  // Full POST body so AuditResult can paint immediately without waiting on GET.
  const [auditSeed, setAuditSeed] = useState<CarePlanAuditPayload | null>(null)

  // Re-keys on any care plan change for a user, not only when the set of users
  // with plans changes — a later upload needs the same post-upload status check.
  const auditHydrateKey = useMemo(
    () =>
      (serviceUsers || [])
        .filter((user: ServiceUserType) => (user.carePlans?.length || 0) > 0)
        .map(
          (user: ServiceUserType) =>
            `${user.id}:${(user.carePlans || []).map(plan => plan._id).join('|')}`,
        )
        .join(','),
    [serviceUsers],
  )

  useEffect(() => {
    setAudits({})
  }, [botId])

  useEffect(() => {
    if (!canAudit || !botId || !auditHydrateKey) return undefined

    const patientIds = auditHydrateKey
      .split(',')
      .map(entry => Number(entry.split(':')[0]))
      .filter(id => !Number.isNaN(id))
    if (!patientIds.length) return undefined

    const controller = new AbortController()
    const timers: ReturnType<typeof setTimeout>[] = []
    const POLL_MS = 3000

    const applyReadyFromAudit = (patientId: number, data: CarePlanAuditPayload | null) => {
      const version = data?.current?.version
      setAudits(prev => {
        if (version != null) {
          if (prev[patientId]?.status === 'READY' && prev[patientId]?.version === version) {
            return prev
          }
          // Overwrite hydrate PROCESSING so "Auditing…" cannot stick after
          // report_generating becomes false.
          return { ...prev, [patientId]: { status: 'READY', version } }
        }
        if (prev[patientId]?.status === 'PROCESSING') {
          const next = { ...prev }
          delete next[patientId]
          return next
        }
        return prev
      })
    }

    const hydratePatient = (patientId: number) => {
      let versionChecked = false
      const tick = () => {
        getCarePlanAuditStatus(botId, patientId, controller.signal)
          .then(generating => {
            if (controller.signal.aborted) return
            if (generating) {
              setAudits(prev => {
                const existing = prev[patientId]
                if (existing?.status === 'PROCESSING') return prev
                return {
                  ...prev,
                  [patientId]: {
                    status: 'PROCESSING',
                    version: existing?.version,
                    serverBusy: true,
                  },
                }
              })
              // Once per patient: learn whether an earlier report exists so the
              // table button can still open it while this run / training is going.
              if (!versionChecked) {
                versionChecked = true
                getCarePlanAudit(botId, patientId, undefined, controller.signal)
                  .then(data => {
                    const version = data?.current?.version
                    if (controller.signal.aborted || version == null) return
                    setAudits(prev => {
                      const existing = prev[patientId]
                      if (existing?.status !== 'PROCESSING' || existing.version != null) return prev
                      return { ...prev, [patientId]: { ...existing, version } }
                    })
                  })
                  .catch(() => null)
              }
              timers.push(setTimeout(tick, POLL_MS))
              return
            }
            getCarePlanAudit(botId, patientId, undefined, controller.signal)
              .then(data => {
                if (controller.signal.aborted) return
                applyReadyFromAudit(patientId, data)
              })
              .catch(error => {
                if (error?.name === 'AbortError') return
              })
          })
          .catch(error => {
            if (error?.name === 'AbortError' || controller.signal.aborted) return
            // Status failed — fall back to GET only (same as pre-status behaviour).
            getCarePlanAudit(botId, patientId, undefined, controller.signal)
              .then(data => {
                if (controller.signal.aborted) return
                applyReadyFromAudit(patientId, data)
              })
              .catch(err => {
                if (err?.name === 'AbortError') return
              })
          })
      }
      tick()
    }

    patientIds.forEach(hydratePatient)

    return () => {
      controller.abort()
      timers.forEach(clearTimeout)
    }
  }, [botId, canAudit, auditHydrateKey])

  const auditBlockedReason = formatCarePlanAuditQuotaReason(auditAllowance)

  /** Resolves true only when the run produced a new report. */
  const handleGenerateAudit = (patientId: number): Promise<boolean> => {
    const user = serviceUsers.find((entry: ServiceUserType) => entry.id === patientId)
    if (hasPendingCarePlan(user?.carePlans)) return Promise.resolve(false)

    // Keep the earlier report's version so the table can still open it.
    setAudits(prev => ({
      ...prev,
      [patientId]: { status: 'PROCESSING', version: prev[patientId]?.version },
    }))

    return generateCarePlanAudit(botId, patientId, {
      patientName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      carePlans: (user?.carePlans || []).map(plan => plan.originalFilename),
      organisation: activeBot?.name,
    })
      .then(result => {
        if (result.ok && result.data?.current && result.data.current.version != null) {
          setAuditSeed(result.data)
          setAudits(prev => ({
            ...prev,
            [patientId]: {
              status: 'READY',
              version: result.data?.current.version,
            },
          }))
          return true
        }
        if (result.quotaExceeded) {
          // Roll back — nothing started; blockedReason on the overlay explains why.
          setAudits(prev => {
            const next = { ...prev }
            const version = prev[patientId]?.version
            if (version != null) next[patientId] = { status: 'READY', version }
            else delete next[patientId]
            return next
          })
          alertError(
            result.message ||
              "This month's care plan audits have all been used. Request a higher limit to continue.",
          )
        } else {
          setAudits(prev => ({
            ...prev,
            [patientId]: { status: 'FAILED', version: prev[patientId]?.version },
          }))
        }
        return false
      })
      .finally(() => usage?.refresh())
  }

  /**
   * Opening the audit screen is navigation only — the job starts from the run
   * control on that screen, so nobody spends an allowance by clicking through
   * from the table. Do not gate on carePlanEnabled here: a READY report must
   * still open when the package later disables new audits (the backend rejects
   * the generate call).
   */
  const openAuditScreen = (user: ServiceUserType) => {
    setAuditSeed(null)
    // Drop a sticky FAILED from a prior run so reopen does not keep the error banner.
    // An earlier report falls back to READY so the table can still open it.
    setAudits(prev => {
      if (prev[user.id]?.status !== 'FAILED') return prev
      const next = { ...prev }
      const version = prev[user.id]?.version
      if (version != null) next[user.id] = { status: 'READY', version }
      else delete next[user.id]
      return next
    })
    setAuditFor(auditSubject(user))
  }

  const handleReportGeneratingChange = useCallback(
    (generating: boolean) => {
      if (!auditFor?.id) return
      const patientId = auditFor.id
      if (generating) {
        setAudits(prev => {
          const existing = prev[patientId]
          // Keep an in-flight PROCESSING; do not resurrect PROCESSING over a
          // client-confirmed FAILED (avoids stacking Reviewing + error banners).
          if (existing?.status === 'PROCESSING' || existing?.status === 'FAILED') return prev
          return {
            ...prev,
            [patientId]: {
              status: 'PROCESSING',
              version: existing?.version,
              serverBusy: true,
            },
          }
        })
        return
      }
      // Generation finished — promote to READY when a version exists, else clear.
      getCarePlanAudit(botId, patientId)
        .then(data => {
          const version = data?.current?.version
          setAudits(prev => {
            if (version != null) {
              return { ...prev, [patientId]: { status: 'READY', version } }
            }
            const next = { ...prev }
            if (next[patientId]?.status === 'PROCESSING') delete next[patientId]
            return next
          })
          if (data) setAuditSeed(data)
        })
        .catch(() => {
          setAudits(prev => {
            const next = { ...prev }
            if (next[patientId]?.status === 'PROCESSING' && next[patientId]?.version == null) {
              delete next[patientId]
            }
            return next
          })
        })
    },
    [auditFor?.id, botId],
  )

  const activeAudit = auditFor && audits[auditFor.id]
  const auditUser = (serviceUsers || []).find(
    (user: ServiceUserType) => user.id === auditFor?.id,
  )

  const handleDownloadAudit = (version?: number) => {
    const v = version ?? activeAudit?.version
    if (!auditFor || v == null) return
    downloadCarePlanAudit(
      botId,
      auditFor.id,
      v,
      `${auditFor.name || 'service-user'}-care-plan-audit.pdf`,
    )
  }

  const onCheckboxChange = (value) => {
    if (!carePlanMutationsAllowed) return
    setAllServiceUsersId()
  }

  const columnTitles = useMemo(() => ([
    {
      component: (
        <Checkbox
          checked={carePlanMutationsAllowed && selectedServiceUsersId.length === serviceUsers.length}
          onChange={onCheckboxChange}
          color='var(--color-button-primary)'
          disabled={!carePlanMutationsAllowed}
          tooltipId='select-all-service-users'
          tooltip={!carePlanMutationsAllowed ? CARE_PLAN_PACKAGE_DISABLED_REASON : undefined}
          tooltipPlace='right'
        />
      ),
      maxWidth: 50
    },
    { title: 'First Name', maxWidth: nameColumnWidth },
    { title: 'Last Name', maxWidth: nameColumnWidth },
    ...(showCareProfessionals ? [{ title: 'Care Professionals', maxWidth: 320 }] : []),
    { title: 'Care plans', maxWidth: carePlansColumnWidth },
    ...(canAudit ? [{ title: 'Care plan audit', maxWidth: AUDIT_COLUMN_WIDTH }] : []),
    ...(showConversationLogs
      ? [{ title: 'Export conversations logs', maxWidth: 220 }]
      : []),
    { title: 'Action', maxWidth: 50 }
  ]), [selectedServiceUsersId.length, showConversationLogs, canAudit, showCareProfessionals, nameColumnWidth, carePlansColumnWidth, carePlanMutationsAllowed])

  const singleCheckboxSelect = (userId: number) => {
    if (!carePlanMutationsAllowed) return
    setSelectedServiceUsersId(userId)
  }

  const deleteCarePlan = (sourceId: string) => {
    if (!carePlanMutationsAllowed) return
    deletePatientCarePlan(botId, sourceId).then(() => {
      getServiceUsers(botId, currentPage, searchValue, rowsPerPage)
    })
  }

  const uploadCarePlan = (event: ChangeEvent<HTMLInputElement>, patientId: number, carePlans) => {
    if (!carePlanMutationsAllowed) return
    const newFiles = Array.from(event.target.files)
    const duplicates = newFiles
      .map(file => file.name)
      .filter(newFileName => carePlans?.map(plan => plan.originalFilename).includes(newFileName))
    const formData = new FormData()
    if (newFiles.length) {
      newFiles.forEach((file) => {
        formData.append('files', file as Blob)
      })
    }

    if (duplicates.length) {
      const message = `File${duplicates.length > 1 ? 's' : ''} ${duplicates.join(', ')} is already added.`
      alertError(message)
    } else {
      setUploadingUserId(patientId)
      onUploadingChange?.(true)
      uploadPatientKnowledge(botId, patientId, formData)
        .then(() => {
          getServiceUsers(botId, currentPage, searchValue, rowsPerPage)
        })
        .finally(() => {
          setUploadingUserId(null)
          onUploadingChange?.(false)
        })
    }
  }

  const openInNewWindow = async (link: string, originalFilename?: string) => {
    try {
      const response = await fetch(link)

      if (!response.ok) {
        throw new Error('Failed to fetch the file')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      const pathWithoutQuery = link.split('?')[0]
      const filenameFromPath = pathWithoutQuery.split('/').pop() || 'downloaded_file'
      const filename = originalFilename || decodeURIComponent(filenameFromPath)

      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error while downloading the file:', error)
      alertError('Failed to download the file')
    }
  }

  return (
    <>
      <TableScroll>
      <Table
        titles={columnTitles}
      >
        <MultipleEditBar
          showMultipleBar={carePlanMutationsAllowed && !!selectedServiceUsersId.length}
          selectedServiceUsersId={selectedServiceUsersId}
          setDeleteUserModal={setDeleteUserModal}
        />
        {serviceUsers.map((user: ServiceUserType) => (
          <TableRow
            key={user.id}
            style={{
            // Top-align cells to the care-plans column so short columns (audit,
            // checkbox, names) stay at the top when the care-plans card is tall.
              alignItems: 'flex-start',
              background: carePlanMutationsAllowed && isChecked(user.id) ? 'linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), rgb(var(--color-primary))' : '',
              position: 'relative',
              ...user.duplicatePatient && { minHeight: 124 }
            }}
          >
            <TableCell
              style={{
                maxWidth: 50,
                minHeight: NAME_CONTROL_HEIGHT,
                marginTop: NAME_CONTROL_TOP_OFFSET,
                alignItems: 'center',
              }}>
              <Checkbox
                checked={carePlanMutationsAllowed && isChecked(user.id)}
                onChange={() => singleCheckboxSelect(user.id)}
                color='var(--color-button-primary)'
                disabled={!carePlanMutationsAllowed}
                tooltipId={`select-service-user-${user.id}`}
                tooltip={!carePlanMutationsAllowed ? CARE_PLAN_PACKAGE_DISABLED_REASON : undefined}
                tooltipPlace='right'
              />
            </TableCell>
            <TableCell
              style={{
                maxWidth: nameColumnWidth,
                minHeight: NAME_CONTROL_HEIGHT,
                marginTop: NAME_CONTROL_TOP_OFFSET,
                alignItems: 'center',
              }}>
              <InputEditor
                user={user}
                value={user.firstName}
                userKey='firstName'
                botId={botId}
              />
              {user.duplicatePatient &&
              <DuplicateWarningMessage style = {{position: 'absolute'}}>
                This name combination already exists. Delete duplicate or add indicator (e.g. middle name) to differentiate service user.
              </DuplicateWarningMessage>
              }
            </TableCell>
            <TableCell
              style={{
                maxWidth: nameColumnWidth,
                minHeight: NAME_CONTROL_HEIGHT,
                marginTop: NAME_CONTROL_TOP_OFFSET,
                alignItems: 'center',
              }}>
              <InputEditor
                user={user}
                value={user.lastName}
                userKey='lastName'
                botId={botId}
              />
            </TableCell>
            {showCareProfessionals && (
              <TableCell style={{ maxWidth: 320 }}>
                <CarersSelect
                  user={user}
                  botId={botId}
                  assignedProfessionals={user.assignedProfessionals}
                  placeholder='Assign care professionals'
                  searchValue={searchValue}
                  rowsPerPage={rowsPerPage}
                />
              </TableCell>
            )}
            <TableCell style={{ maxWidth: carePlansColumnWidth, minWidth: 0, overflow: 'hidden' }}>
              <CarePlans
                carePlans={user.carePlans}
                expandFilename={isQcs}
                mutationsDisabled={!carePlanMutationsAllowed}
                patientId={user.id}
                onDeleteAction={deleteCarePlan}
                onUploadAction={(e) => uploadCarePlan(e, user.id, user.carePlans)}
                onOpenInNewWindowAction={openInNewWindow}
              />
            </TableCell>
            {canAudit && (
              <TableCell style={{ maxWidth: AUDIT_COLUMN_WIDTH }}>
                <CarePlanAudit
                  patientId={user.id}
                  carePlanCount={user.carePlans?.length || 0}
                  audit={audits[user.id]}
                  onOpen={() => openAuditScreen(user)}
                  blockedReason={auditBlockedReasonForUser(
                    user.carePlans,
                    auditBlockedReason,
                    { packageEnabled: carePlanMutationsAllowed },
                  )}
                />
              </TableCell>
            )}
            {showConversationLogs && (
              <TableCell style={{ maxWidth: 220, marginRight: 10 }}>
                <ExportConversations
                  botId={botId}
                  userId={user.id}
                />
              </TableCell>
            )}
            <TableCell style={{ maxWidth: 50 }}>

            </TableCell>
          </TableRow>
        ))}
      </Table>
      </TableScroll>
      <AuditResult
        open={!!auditFor}
        onClose={() => {
          setAuditFor(null)
          setAuditSeed(null)
        }}
        patientName={auditFor?.name || ''}
        filename={auditFor?.filename || ''}
        carePlans={auditFor?.carePlans}
        carePlanFiles={auditUser?.carePlans}
        botId={botId}
        patientId={auditFor?.id}
        status={activeAudit?.status}
        knownVersion={activeAudit?.version}
        seedPayload={auditSeed}
        onRun={() => (auditFor ? handleGenerateAudit(auditFor.id) : Promise.resolve(false))}
        onDownload={handleDownloadAudit}
        onReportGeneratingChange={handleReportGeneratingChange}
      />
    </>
  )
}
